/**
 * Expanded Item Content
 *
 * Renders the full content of a table or linked table in the expanded overlay.
 * Includes a consistent header with title, description, and close button.
 *
 * When opened from URL (name not available), fetches item data for the header.
 */

import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button, Skeleton } from '@loader/ui';
import type { TableApiResponse, LinkedTableApiResponse } from '@loader/shared-types';

import type { PreviewItem } from './PreviewPanel/types';
import { ConnectorLabel } from './ConnectorLabel';

import { ViewTableContent } from '@/features/tables/pages/ViewTable';
import EditLinkedTableContent from '@/features/linkedTables/editLinkedTable/EditLinkedTable';
import { useSidePanelStore } from '@/features/workspace/stores';
import { useResolvedConnectorType } from '@/features/workspace/hooks/useResolvedConnectorType';
import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';

interface ExpandedItemContentProps {
  item: PreviewItem;
  /** Whether the user can edit (false for viewers) */
  canEdit?: boolean;
}

export function ExpandedItemContent({ item, canEdit = true }: ExpandedItemContentProps) {
  const collapse = useSidePanelStore((state) => state.collapse);
  const rpc = useRpc();

  // Fetch table details if name is not available (URL restore case)
  const { data: tableDetails, isLoading: isLoadingTable } = useQuery({
    queryKey: queryKeys.tables.detail(item.id),
    queryFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.tables[':id'].$get({
        param: { id: item.id },
      });
      if (!res.ok) throw new Error('Failed to fetch table');
      const data = (await res.json()) as { table: TableApiResponse };
      return data.table;
    },
    enabled: item.type === 'table' && !item.name,
  });

  // Fetch linked table details if name is not available (URL restore case)
  const { data: linkedTableDetails, isLoading: isLoadingLinkedTable } = useQuery({
    queryKey: queryKeys.linkedTables.detail(item.id),
    queryFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private['linked-tables'][':id'].$get({
        param: { id: item.id },
      });
      if (!res.ok) throw new Error('Failed to fetch linked table');
      const data = (await res.json()) as { linkedTable: LinkedTableApiResponse };
      return data.linkedTable;
    },
    enabled: item.type === 'linked_table' && !item.name,
  });

  // Resolve connector type from tables list cache (detail endpoint doesn't include it)
  const displayConnectorType = useResolvedConnectorType(item.id, item.type, item.connectorType);

  // Use fetched data if item.name is not available
  const displayName = item.name || (item.type === 'table' ? tableDetails?.name : linkedTableDetails?.name) || '';
  const displayDescription = item.description ?? (item.type === 'table' ? tableDetails?.description : linkedTableDetails?.description);
  const isLoadingHeader = !item.name && (isLoadingTable || isLoadingLinkedTable);

  const renderContent = () => {
    if (item.type === 'table') {
      return <ViewTableContent tableId={item.id} canEdit={canEdit} />;
    }

    if (item.type === 'linked_table') {
      return <EditLinkedTableContent linkedTableId={item.id} canEdit={canEdit} />;
    }

    return null;
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="shrink-0 border-b px-4 py-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            {isLoadingHeader ? (
              <>
                <Skeleton className="h-7 w-48" />
                <Skeleton className="mt-1.5 h-4 w-64" />
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  <h1 className="truncate text-xl font-semibold">{displayName}</h1>
                  {displayConnectorType && (
                    <ConnectorLabel connectorType={displayConnectorType} size="sm" />
                  )}
                </div>
                {displayDescription && (
                  <p className="mt-0.5 truncate text-sm text-muted-foreground">
                    {displayDescription}
                  </p>
                )}
              </>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="shrink-0"
            onClick={collapse}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="relative min-h-0 flex-1">
        {renderContent()}
      </div>
    </div>
  );
}
